import { Formik } from 'formik'
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  ContractorPortalSignInEnterCode,
  ContractorPortalSignInResendCode,
  SignInEnterCode,
  SignInResendCode,
} from 'src/constants/actionTypes'
import { Button } from 'src/components/shared/Button'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { ResendCode } from 'src/components/shared/ResendCode'
import { createAsyncAction } from 'src/utils/reduxUtils'
import tw from 'twin.macro'
import { OtpInputFormValues } from './OtpInputForm.interface'
import { OtpInputFormSchema } from './OtpInputForm.validator'
import { SignInPhoneResponse } from 'src/interfaces/auth'
import { OtpInputFields } from './OtpInputFields'
import { handleApiError } from 'src/utils/errorHandlers'
import { getContractorPortalValidateLockedUntil, getValidateLockedUntil } from 'src/selectors/auth'
import { Typography } from '@mui/material'
import { DateTime } from 'luxon'

interface Props {
  partnerName: string
  phone: string
  platform: 'client' | 'contractorPortal'
}

const initialValues: OtpInputFormValues = {
  code: '',
}

export const OtpInputForm = ({ partnerName, phone, platform }: Props) => {
  const validateLockedRemainingInterval = useRef<NodeJS.Timer | null>(null)

  const dispatch = useDispatch()

  const clientValidateLockedUntil = useSelector(getValidateLockedUntil)
  const contractorPortalValidateLockedUntil = useSelector(getContractorPortalValidateLockedUntil)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [validateLockedRemaining, setValidateLockedRemaining] = useState<number>(0)

  const validateLockedUntil =
    platform === 'contractorPortal'
      ? contractorPortalValidateLockedUntil
      : clientValidateLockedUntil

  useEffect(() => {
    if (!validateLockedUntil) {
      setValidateLockedRemaining(0)

      return
    }

    const updateRemaining = () => {
      const remainingMs = Math.max(
        0,
        DateTime.fromISO(validateLockedUntil, { zone: 'utc' }).diffNow().toMillis(),
      )

      setValidateLockedRemaining(remainingMs)

      return remainingMs
    }

    if (updateRemaining() <= 0) {
      return
    }

    validateLockedRemainingInterval.current = setInterval(() => {
      if (updateRemaining() <= 0 && validateLockedRemainingInterval.current) {
        clearInterval(validateLockedRemainingInterval.current)

        validateLockedRemainingInterval.current = null
      }
    }, 1000)

    return () => {
      if (validateLockedRemainingInterval.current) {
        clearInterval(validateLockedRemainingInterval.current)

        validateLockedRemainingInterval.current = null
      }
    }
  }, [validateLockedUntil])

  const handleFormSubmit = async (values: OtpInputFormValues) => {
    setIsLoading(true)
    setError(null)

    try {
      if (platform === 'client') {
        await createAsyncAction(
          dispatch,
          SignInEnterCode.request({ ...values, phone, partnerName }),
        )
      }

      if (platform === 'contractorPortal') {
        await createAsyncAction(
          dispatch,
          ContractorPortalSignInEnterCode.request({ ...values, phone, partnerName }),
        )
      }
    } catch (submitError: any) {
      setError(submitError)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = useCallback(async () => {
    try {
      if (platform === 'client') {
        const response: SignInPhoneResponse = await createAsyncAction(
          dispatch,
          SignInResendCode.request({ phone, partnerName, resend: true }),
        )

        return response
      }

      if (platform === 'contractorPortal') {
        const response: SignInPhoneResponse = await createAsyncAction(
          dispatch,
          ContractorPortalSignInResendCode.request({ phone, partnerName, resend: true }),
        )

        return response
      }
    } catch (resendError: any) {
      handleApiError(resendError)
    }
  }, [phone, partnerName, platform, dispatch])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={OtpInputFormSchema}
    >
      {({ handleSubmit }) => {
        return (
          <div>
            <OtpInputFields />
            <FormErrorMessage css={tw`mb-6`} error={error} />
            {validateLockedRemaining ? (
              <Typography
                variant="body2"
                css={tw`flex flex-row justify-center text-warning-500 text-center`}
              >
                {`Too many incorrect verification attempts. Your account has been locked. Please try again in
        ${DateTime.fromMillis(Math.ceil(validateLockedRemaining / 1000) * 1000).toFormat(
          'mm:ss',
        )}.`}
              </Typography>
            ) : (
              <Fragment>
                <Button
                  className={tw`w-full mb-6`}
                  loading={isLoading}
                  onClick={() => {
                    handleSubmit()
                  }}
                  data-testid="sign-in-enter-code-continue-btn"
                >
                  Continue
                </Button>
                <ResendCode
                  platform={platform}
                  resendLinkTestId="sign-in-enter-code-resend-link"
                  onResend={handleResend}
                />
              </Fragment>
            )}
          </div>
        )
      }}
    </Formik>
  )
}
